import type { StateCode } from "./types";

export class SteuernummerError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SteuernummerError";
  }
}

// --- Parsing errors (thrown by parseSteuernummer) ---

export class AllowedCharactersError extends SteuernummerError {
  constructor() {
    super('Bitte nur Ziffern, "-", "_", "/" oder Leerzeichen verwenden');
    this.name = "AllowedCharactersError";
  }
}

export class TooShortError extends SteuernummerError {
  constructor() {
    super("Eine Steuernummer muss mindestens 10 Ziffern enthalten");
    this.name = "TooShortError";
  }
}

export class TooLongError extends SteuernummerError {
  constructor() {
    super("Eine Steuernummer darf maximal 13 Ziffern enthalten");
    this.name = "TooLongError";
  }
}

export class WrongLengthForStateError extends SteuernummerError {
  readonly state: StateCode;

  constructor(state: StateCode) {
    super("Die Steuernummer hat die falsche Länge");
    this.name = "WrongLengthForStateError";
    this.state = state;
  }
}

export class MissingZeroError extends SteuernummerError {
  constructor() {
    super(
      'Steuernummern zur elektronischen Übermittlung müssen an 5. Stelle eine "0" aufweisen'
    );
    this.name = "MissingZeroError";
  }
}

export class UnknownStatePrefixError extends SteuernummerError {
  constructor() {
    super("Die Steuernummer kann keinem Bundesland zugewiesen werden");
    this.name = "UnknownStatePrefixError";
  }
}

export class MissingStateInformationError extends SteuernummerError {
  constructor() {
    super("Unzureichende Informationen über das zugehörige Bundesland");
    this.name = "MissingStateInformationError";
  }
}

export class WrongStateError extends SteuernummerError {
  readonly expected: StateCode;
  readonly actual: StateCode[];

  constructor(expected: StateCode, actual: StateCode[]) {
    super("Die Steuernummer entspricht nicht dem angegebenen Bundesland");
    this.name = "WrongStateError";
    this.expected = expected;
    this.actual = actual;
  }
}

export class ParseError extends SteuernummerError {
  constructor() {
    super("Die Steuernummer konnte nicht geparsed werden");
    this.name = "ParseError";
  }
}

// --- Validation errors (thrown internally by validateSteuernummer) ---

export class UnknownFinanzamtError extends SteuernummerError {
  readonly finanzamtNummer: string;

  constructor(finanzamtNummer: string) {
    super("Die Steuernummer kann keinem Finanzamt zugewiesen werden");
    this.name = "UnknownFinanzamtError";
    this.finanzamtNummer = finanzamtNummer;
  }
}

export class InvalidBezirksnummerError extends SteuernummerError {
  readonly bezirksnummer: string;

  constructor(bezirksnummer: string) {
    super("Die Steuernummer enthält eine invalide Bezirksnummer");
    this.name = "InvalidBezirksnummerError";
    this.bezirksnummer = bezirksnummer;
  }
}

export class InvalidPruefzifferError extends SteuernummerError {
  readonly expected: number[];
  readonly actual: string;

  constructor(expected: number[], actual: string) {
    super("Die Prüfziffer der Steuernummer stimmt nicht");
    this.name = "InvalidPruefzifferError";
    this.expected = expected;
    this.actual = actual;
  }
}

export class NwInternalConsistencyError extends SteuernummerError {
  constructor() {
    super("Die interne Konsistenz der Steuernummer ist nicht gegeben");
    this.name = "NwInternalConsistencyError";
  }
}

// --- Normalization errors (thrown by normalizeSteuernummer) ---

export class InvalidDigitCountError extends SteuernummerError {
  readonly state: StateCode;
  readonly expectedLength: number;

  constructor(state: StateCode, expectedLength: number) {
    super(
      `\`steuernummer\` for ${state} must contain exactly ${expectedLength} digits`
    );
    this.name = "InvalidDigitCountError";
    this.state = state;
    this.expectedLength = expectedLength;
  }
}

export class InvalidStateCodeError extends SteuernummerError {
  readonly state: string;

  constructor(state: string) {
    super("`state` must be a German ISO 3166-2 code");
    this.name = "InvalidStateCodeError";
    this.state = state;
  }
}
